/**
 * SA 主導の報酬請求書 (commission_invoices) (v3.2.14 / Phase 3a)。
 *
 * 物理コレクション: commission_invoices/{invoiceId}
 *
 * 概念:
 *   - 弊社 → SA への報酬支払いは、SA が複数の commission_records を集約した
 *     「請求書」を弊社経理に提出する形式 (B2B 外注委託の通常運用)
 *   - SA は /sa/deals の「請求可能タブ」で自分の status='pending' レコードを
 *     複数選択し、ボタン一つで請求書を発行 + 経理担当にメール送信
 *   - 経理は公開URL (トークン付き) で請求書を閲覧、銀行振込実行後、
 *     管理者画面 /sales/sa-invoices で「支払済マーク」 → 請求書本体 +
 *     含まれる commission_records が一括で status='paid' に遷移
 *
 * 状態遷移:
 *   draft (作成中、未使用予約) → sent (発行済、経理にメール送信完了)
 *     → paid (経理が振込確認・マーク) → cancelled (任意で取消)
 *
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md (v3.2.14 / Phase 3a)
 */
import type { BankAccount, IsoOrTimestamp } from './common';

export type CommissionInvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled';

export const COMMISSION_INVOICE_STATUS_LABELS: Record<CommissionInvoiceStatus, string> = {
  draft: '作成中',
  // 2026-10-07: 「送付済」→「請求済」(個別説明 実施状況タブの依頼表記に統一)
  sent: '請求済',
  paid: '支払済',
  cancelled: '取消',
};

/**
 * 請求書明細行 — 含まれる各 commission_record / 個別説明代行 の snapshot。
 * v3.2.17: kind フィールドで「成約報酬」「個別説明代行」を区別。
 */
export type CommissionInvoiceLineItemKind = 'sales_commission' | 'briefing_fee';

export interface CommissionInvoiceLineItem {
  /** v3.2.17: 明細行の種別 */
  kind?: CommissionInvoiceLineItemKind;
  /** 元 commission_record のフルパス (kind='sales_commission' のみ) */
  recordPath?: string;
  /** 元 record の Firestore ID (kind='sales_commission' のみ) */
  recordId?: string;
  /** 由来商談 ID (kind='sales_commission' のみ) */
  dealId?: string;
  dealTitle?: string;
  /** 顧客 (kind='sales_commission' のみ) */
  contactName?: string;
  /** 報酬対象商品 / または「個別説明代行 ×N件」のような表示文字列 */
  productName: string;
  /** 報酬額 (税抜、最終金額) */
  amount: number;
  /** 入金日 (kind='sales_commission' のみ) */
  paidAt?: IsoOrTimestamp;

  // ─── kind='briefing_fee' の場合のフィールド ───
  /** 1 件あたり代行料 (個別設定 override.briefingFee) */
  unitPrice?: number;
  /** 含まれる件数 */
  quantity?: number;
  /** 含まれる briefing_records の (dealId/recordId) リスト */
  briefingRecordRefs?: Array<{ dealId: string; recordId: string }>;
}

/**
 * 税率設定。v3.2.14 では消費税 10% 固定。
 * 免税事業者の SA は taxRate=0、消費税欄を表示しない。
 */
export interface CommissionInvoiceTax {
  /** 0〜100 の整数 (% 表記)。免税事業者は 0 */
  rate: number;
  /** 消費税額 (税抜合計 × rate / 100) */
  amount: number;
}

export interface CommissionInvoice {
  // ─── 受取人 (SA) ───
  /** admins/{uid} */
  recipientId: string;
  recipientName: string;
  recipientEmail: string | null;
  /** インボイス番号 snapshot (発行時点の admin.invoiceRegistrationNumber) */
  invoiceRegistrationNumber: string | null;
  /** 振込先口座 snapshot (発行時点の default bankAccount) */
  bankAccountSnapshot: {
    label?: string | null;
    bankName: string;
    branchName: string;
    accountType: BankAccount['accountType'];
    accountNumber: string;
    accountHolder: string;
  } | null;

  // ─── 請求書本体 ───
  /** 表示用の請求番号 (例: "SA-2026-05-001") — 発行時に自動採番 */
  invoiceNumber: string;
  /** 請求対象期間ラベル (任意、例: "2026年5月分") */
  periodLabel?: string | null;
  status: CommissionInvoiceStatus;
  /** 明細行 */
  lineItems: CommissionInvoiceLineItem[];
  /** 税抜小計 = sum(lineItems.amount) */
  subtotalExcludingTax: number;
  /** 消費税 (10% 固定、免税事業者は 0) */
  tax: CommissionInvoiceTax;
  /** 税込合計 = subtotalExcludingTax + tax.amount */
  totalIncludingTax: number;

  // ─── 発行・送付・支払い ───
  /** 発行日 */
  issuedAt: IsoOrTimestamp;
  /** 経理にメール送信した日時 (status='sent' 遷移時) */
  sentAt?: IsoOrTimestamp | null;
  /** 経理が振込確認・支払マークした日時 */
  paidAt?: IsoOrTimestamp | null;
  /** 支払マーク実行者 (admins/{uid}) */
  paidBy?: string | null;
  /** 取消日 */
  cancelledAt?: IsoOrTimestamp | null;

  // ─── 公開アクセス ───
  /** トークン付き URL アクセス用 (/c/sa-invoice/:token) */
  publicToken: string;
  /** 公開有効期限 (任意、未設定なら無期限) */
  publicValidUntil?: IsoOrTimestamp | null;

  // ─── 任意 ───
  memo?: string | null;

  // ─── 標準フィールド ───
  createdAt: IsoOrTimestamp;
  createdBy: string;
  updatedAt: IsoOrTimestamp;
  updatedBy: string;
}
